import React, { useState, useEffect, useCallback } from "react";
import { get, post, patch, del } from "../../helpers/api_helper";
import Dropzone from "react-dropzone-uploader";
import "react-dropzone-uploader/dist/styles.css";
import toastr from "toastr";
import "toastr/build/toastr.min.css";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import ClassicEditor from "@ckeditor/ckeditor5-build-classic";
import {
  Modal, ModalHeader, ModalBody, ModalFooter,
  Nav, NavItem, NavLink, TabContent, TabPane,
  Form, FormGroup, Label, Input, Button, Spinner,
  Row, Col,
} from "reactstrap";
import { makeUploadParams } from "../../helpers/imageResize";

const ODATA_BASE  = "https://buharaapi.stechomeyazilim.info";
const UPLOAD_URL  = "https://buharaadmin.stechomeyazilim.info:8099/uploadfile";
const IMG_BASE    = "https://buharasubdomain.stechomeyazilim.info/";

const LANGS = [
  { code: "en", label: "English", flag: "gb" },
  { code: "fr", label: "Français", flag: "fr" },
  { code: "de", label: "Deutsch", flag: "de" },
  { code: "nl", label: "Nederlands", flag: "nl" },
];

/**
 * Evrensel Çok Dilli İçerik Yönetim Modalı.
 *
 * Props:
 *  isOpen         – Modal açık mı?
 *  toggle         – Modal açma/kapama fonksiyonu
 *  title          – Modal başlığı (ör. "Slider Çevirileri")
 *  tableName      – OData tablo adı (ör. "Slider_Lang", "Blog_Lang", "Products_Desc")
 *  foreignKey     – FK kolon adı (ör. "Slider_ID", "Blog_ID", "Products_ID")
 *  foreignKeyValue – Ana kayıt ID'si
 *  fields         – Düzenlenecek alanlar dizisi:
 *    [
 *      { name: 'SliderImage', label: 'Görsel',  type: 'image' },
 *      { name: 'Slider_Text', label: 'Başlık',  type: 'text' },
 *      { name: 'Slider_Link', label: 'Link',    type: 'text' },
 *      { name: 'Blog_Desc',   label: 'Açıklama',type: 'textarea' },
 *      { name: 'Recipes_Video', label: 'Video',  type: 'video' },
 *    ]
 */
export default function LangModal({
  isOpen, toggle, title, tableName, foreignKey, foreignKeyValue, fields = [],
  sourceRecord = null,
}) {
  const [activeTab, setActiveTab]     = useState("en");
  const [records,   setRecords]       = useState({});   // { en: {...}, fr: {...}, ... }
  const [formData,  setFormData]      = useState({});   // { en: {field:val}, fr: ... }
  const [imageMap,  setImageMap]      = useState({});   // { en: { fieldName: filename } }
  const [loading,   setLoading]       = useState(false);
  const [saving,    setSaving]        = useState(false);

  // ── Veri Çekme ────────────────────────────────────────────────
  const fetchRecords = useCallback(async () => {
    if (!foreignKeyValue) return;
    setLoading(true);
    try {
      const filter = encodeURIComponent(`${foreignKey} eq ${foreignKeyValue}`);
      const res = await get(`${ODATA_BASE}/${tableName}?$filter=${filter}`);
      const items = res?.value || [];
      const map   = {};
      const fmap  = {};
      const imap  = {};
      items.forEach(item => {
        const code = (item.Lang_Code || "").toLowerCase();
        map[code]  = item;
        fmap[code] = buildFormInit(item);
        const imgInit = {};
        fields.forEach(f => {
          if ((f.type === 'image' || f.type === 'file' || f.type === 'video') && item[f.name]) {
            imgInit[f.name] = item[f.name];
          }
        });
        imap[code] = imgInit;
      });
      setRecords(map);
      setFormData(fmap);
      setImageMap(imap);
    } catch (err) {
      console.error("LangModal fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [tableName, foreignKey, foreignKeyValue, fields]);

  useEffect(() => {
    if (isOpen) { fetchRecords(); setActiveTab("en"); setImageMap({}); }
  }, [isOpen, fetchRecords]);

  // Form için başlangıç değerlerini mevcut kayıttan al
  const buildFormInit = (record = {}) => {
    const init = {};
    fields.forEach(f => {
      const primary = record[f.name];
      const mirrored = f.mirrorTo ? record[f.mirrorTo] : "";
      init[f.name] = primary || mirrored || "";
      if (f.typeField) {
        init[f.typeField] = record[f.typeField] || "";
      }
    });
    return init;
  };

  // ── Form Güncelleme ────────────────────────────────────────────
  const handleChange = (lang, fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [lang]: { ...(prev[lang] || {}), [fieldName]: value },
    }));
  };

  // ── Görsel Yükleme ─────────────────────────────────────────────
  // Görseller sunucuya gitmeden önce tarayıcıda küçültülür (helpers/imageResize).
  const getUploadParams = makeUploadParams(UPLOAD_URL);

  const handleImageUpload = (lang, fieldName) => ({ xhr }) => {
    if (!xhr) return;
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        try {
          const result = JSON.parse(xhr.response);
          const filename = result.file.filename;
          setImageMap(prev => ({
            ...prev,
            [lang]: { ...(prev[lang] || {}), [fieldName]: filename },
          }));
          handleChange(lang, fieldName, filename);
        } catch {
          toastr.error("Görsel yükleme sırasında hata oluştu.");
        }
      }
    };
  };

  const handleFileUpload = (lang, fieldName, typeFieldName) => ({ xhr, file }) => {
    if (!xhr) return;
    xhr.onreadystatechange = () => {
      if (xhr.readyState === 4) {
        try {
          const result = JSON.parse(xhr.response);
          const filename = result.file.filename;
          setImageMap(prev => ({
            ...prev,
            [lang]: { ...(prev[lang] || {}), [fieldName]: filename },
          }));
          handleChange(lang, fieldName, filename);
          if (typeFieldName && file?.type) {
            handleChange(lang, typeFieldName, file.type);
          }
        } catch {
          toastr.error("Dosya yükleme sırasında hata oluştu.");
        }
      }
    };
  };

  // ── Kaydet ────────────────────────────────────────────────────
  const handleSave = async (lang) => {
    setSaving(true);
    try {
      const existing = records[lang];

      // Sadece fields dizisinde tanımlı alanları payload'a ekle
      const payloadFields = {};
      fields.forEach(f => {
        const val = formData[lang]?.[f.name] ?? "";
        payloadFields[f.name] = val;
        if (f.mirrorTo) payloadFields[f.mirrorTo] = val;
        if (f.typeField) {
          payloadFields[f.typeField] = formData[lang]?.[f.typeField] ?? "";
        }
      });

      const payload = {
        [foreignKey]: foreignKeyValue,
        Lang_Code: lang,
        ...payloadFields,
      };

      console.log("Saving payload:", payload);
      if (existing?.ID) {
        // PATCH — güncelle
        await patch(`${ODATA_BASE}/${tableName}(${existing.ID})`, payload);
        toastr.success(`${lang.toUpperCase()} çevirisi güncellendi.`);
      } else {
        // POST — yeni ekle
        await post(`${ODATA_BASE}/${tableName}`, payload);
        toastr.success(`${lang.toUpperCase()} çevirisi eklendi.`);
      }
      await fetchRecords();
    } catch (err) {
      const detail = err.response?.data?.error?.message || err.response?.data || err.message;
      toastr.error("Kayıt sırasında hata oluştu: " + detail);
      console.error("LangModal save error:", err.response?.data || err.message);
    } finally {
      setSaving(false);
    }
  };

  // ── Sil ───────────────────────────────────────────────────────
  const handleDelete = async (lang) => {
    const existing = records[lang];
    if (!existing?.ID) return;
    if (!window.confirm(`${lang.toUpperCase()} çevirisini silmek istiyor musunuz?`)) return;
    setSaving(true);
    try {
      await del(`${ODATA_BASE}/${tableName}(${existing.ID})`);
      toastr.success(`${lang.toUpperCase()} çevirisi silindi.`);
      await fetchRecords();
      setFormData(prev => ({ ...prev, [lang]: {} }));
    } catch {
      toastr.error("Silme sırasında hata oluştu.");
    } finally {
      setSaving(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────
  const renderField = (lang, field) => {
    const val     = formData[lang]?.[field.name] || "";
    const imgFile = imageMap[lang]?.[field.name] || records[lang]?.[field.name];

    if (field.type === "image") {
      return (
        <FormGroup key={field.name}>
          <Label>{field.label}</Label>
          {imgFile && (
            <div className="mb-2">
              <img
                src={IMG_BASE + imgFile}
                alt=""
                style={{ height: 80, objectFit: "contain", borderRadius: 6, border: "1px solid #ddd" }}
              />
            </div>
          )}
          <Dropzone
            getUploadParams={getUploadParams}
            onChangeStatus={handleImageUpload(lang, field.name)}
            maxFiles={1}
            accept="image/*"
            styles={{
              dropzone: { minHeight: 60, border: "1px dashed #aaa", background: "#f9f9f9" },
            }}
            inputContent="Görsel seçin veya sürükleyin"
          />
        </FormGroup>
      );
    }

    if (field.type === "video") {
      return (
        <FormGroup key={field.name}>
          <Label>{field.label}</Label>
          {imgFile && (
            <div className="mb-2">
              <video
                src={IMG_BASE + imgFile}
                controls
                preload="metadata"
                style={{ height: 140, maxWidth: "100%", borderRadius: 6, border: "1px solid #ddd", background: "#000" }}
              />
            </div>
          )}
          <Dropzone
            getUploadParams={getUploadParams}
            onChangeStatus={handleImageUpload(lang, field.name)}
            maxFiles={1}
            accept="video/*"
            styles={{
              dropzone: { minHeight: 60, border: "1px dashed #aaa", background: "#f9f9f9" },
            }}
            inputContent="Video seçin veya sürükleyin"
          />
        </FormGroup>
      );
    }

    if (field.type === "file") {
      const isPdf = (imgFile || "").toLowerCase().endsWith(".pdf");
      return (
        <FormGroup key={field.name}>
          <Label>{field.label}</Label>
          {imgFile && (
            <div className="mb-2">
              {isPdf ? (
                <a
                  href={IMG_BASE + imgFile}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-sm btn-outline-primary"
                >
                  📄 PDF Görüntüle
                </a>
              ) : (
                <img
                  src={IMG_BASE + imgFile}
                  alt=""
                  style={{ height: 80, objectFit: "contain", borderRadius: 6, border: "1px solid #ddd" }}
                />
              )}
            </div>
          )}
          <Dropzone
            getUploadParams={getUploadParams}
            onChangeStatus={handleFileUpload(lang, field.name, field.typeField)}
            maxFiles={1}
            accept="image/*,application/pdf"
            styles={{
              dropzone: { minHeight: 60, border: "1px dashed #aaa", background: "#f9f9f9" },
            }}
            inputContent="Dosya seçin veya sürükleyin (PDF / Görsel)"
          />
        </FormGroup>
      );
    }

    if (field.type === "textarea") {
      return (
        <FormGroup key={field.name}>
          <Label>{field.label}</Label>
          <Input
            type="textarea"
            rows={4}
            value={val}
            onChange={e => handleChange(lang, field.name, e.target.value)}
            placeholder={field.label}
          />
        </FormGroup>
      );
    }

    if (field.type === "ckeditor") {
      return (
        <FormGroup key={field.name}>
          <Label>{field.label}</Label>
          <CKEditor
            editor={ClassicEditor}
            data={val}
            onChange={(event, editor) => {
              handleChange(lang, field.name, editor.getData());
            }}
          />
        </FormGroup>
      );
    }

    // type === "text" (default)
    return (
      <FormGroup key={field.name}>
        <Label>{field.label}</Label>
        <Input
          type="text"
          value={val}
          onChange={e => handleChange(lang, field.name, e.target.value)}
          placeholder={field.label}
        />
      </FormGroup>
    );
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" fade={false}>
      <ModalHeader toggle={toggle}>
        🌍 {title} — Dil Çevirileri
      </ModalHeader>

      <ModalBody>
        {loading ? (
          <div className="text-center py-4">
            <Spinner color="warning" /> <span className="ms-2">Yükleniyor...</span>
          </div>
        ) : (
          <>
            {/* Dil Sekmeleri */}
            <Nav tabs className="mb-3">
              {LANGS.map(l => (
                <NavItem key={l.code}>
                  <NavLink
                    className={activeTab === l.code ? "active fw-bold" : ""}
                    onClick={() => setActiveTab(l.code)}
                    style={{ cursor: "pointer" }}
                  >
                    <img
                      src={`https://flagcdn.com/w20/${l.flag}.png`}
                      alt=""
                      width={18}
                      height={12}
                      style={{ marginRight: 6, borderRadius: 2, objectFit: "cover" }}
                    />
                    {l.label}
                    {records[l.code] && (
                      <span className="ms-1 badge bg-success" style={{ fontSize: 9 }}>✓</span>
                    )}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            {/* Tab İçerikleri */}
            <TabContent activeTab={activeTab}>
              {LANGS.map(l => (
                <TabPane tabId={l.code} key={l.code}>
                  {sourceRecord && (
                    <div className="alert alert-light border mb-3 py-2">
                      <div className="fw-bold mb-1" style={{ fontSize: 12 }}>
                        🇹🇷 Türkçe (kaynak içerik)
                      </div>
                      {fields.map(f => {
                        const raw = sourceRecord[f.name] || sourceRecord[f.mirrorTo] || "";
                        if (!raw) return null;
                        const text = String(raw).replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim();
                        if (!text) return null;
                        return (
                          <div key={f.name} className="mb-1" style={{ fontSize: 12 }}>
                            <span className="text-muted">{f.label}: </span>
                            {text.slice(0, 280)}{text.length > 280 ? "…" : ""}
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <Form>
                    {fields.map(field => renderField(l.code, field))}
                  </Form>

                  <div className="d-flex gap-2 mt-3">
                    <Button
                      color="warning"
                      disabled={saving}
                      onClick={() => handleSave(l.code)}
                    >
                      {saving ? <Spinner size="sm" /> : null}
                      {" "}{records[l.code] ? "Güncelle" : "Kaydet"}
                    </Button>
                    {records[l.code] && (
                      <Button
                        color="danger"
                        outline
                        disabled={saving}
                        onClick={() => handleDelete(l.code)}
                      >
                        Sil
                      </Button>
                    )}
                  </div>
                </TabPane>
              ))}
            </TabContent>
          </>
        )}
      </ModalBody>

      <ModalFooter>
        <Button color="secondary" onClick={toggle}>Kapat</Button>
      </ModalFooter>
    </Modal>
  );
}
